import { useMemo, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchAccountAudit, toUnifiedFromAccountAudit } from '@/api/console';
import { auditOrderingParam } from './auditFilterTypes';
import { resolveDateRange } from './Filters';
import { useAuditFilters } from './useAuditFilters';
import { AuditPanelShell } from './AuditPanelShell';

/**
 * EAA-1821: the "Tài khoản" source — server-paginated `AccountAudit` via
 * `GET /api/v1/accounts/audit`.
 *
 * This component is now only the ENDPOINT: filter/sort/page state lives in
 * `useAuditFilters` (URL-backed) and every pixel lives in `AuditPanelShell`,
 * which `AdminAuditPanel` renders too. The two panels were previously a
 * copy-paste pair that had already drifted apart.
 *
 * When `lockedUsername` is set (the account profile tab) the subject filter is
 * pinned to that account, so a per-account log cannot be widened into the
 * global one from inside the profile.
 */
export function AccountAuditPanel({
    lockedUsername,
}: { lockedUsername?: string } = {}): ReactElement | null {
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const model = useAuditFilters();
    const { debouncedFilters, sort, page } = model;

    // EAA-1821: the request is built from the DEBOUNCED filters — the previous
    // version fed the raw username box straight into the query key, so every
    // keystroke fired a request against an append-only audit table.
    const params = useMemo(() => {
        const range = resolveDateRange(debouncedFilters.date);
        return {
            page,
            q: debouncedFilters.search.trim() || undefined,
            action: debouncedFilters.action,
            success: debouncedFilters.success,
            credential_mode: debouncedFilters.credentialMode,
            username: lockedUsername ?? (debouncedFilters.target.trim() || undefined),
            actor: debouncedFilters.actor.trim() || undefined,
            from: range.from,
            to: range.to,
            ordering: auditOrderingParam(sort),
        };
    }, [debouncedFilters, sort, page, lockedUsername]);

    const enabled = authenticated && Boolean(accessToken) && can('console.audit.view');
    const query = useQuery({
        queryKey: ['accounts-audit', params, accessToken],
        queryFn: () => fetchAccountAudit(config.apiBase, accessToken as string, params),
        enabled,
        retry: false,
    });

    const data = useMemo(
        () => ({
            rows: query.data?.results.map(toUnifiedFromAccountAudit) ?? [],
            // Server-owned: `count` is the total across every page, never the
            // length of the page in hand.
            total: query.data?.count ?? 0,
            totalPages: query.data?.total_pages ?? 1,
            pageSize: query.data?.page_size ?? 20,
        }),
        [query.data]
    );

    if (!can('console.audit.view')) {
        return null;
    }

    return (
        <AuditPanelShell
            kind="account"
            model={model}
            data={data}
            query={query}
            lockedTarget={lockedUsername}
        />
    );
}
